import React from 'react';
import {
    Box,
    Flex,
    HStack,
    IconButton,
    Image,
    Text,
    Tooltip,
    Alert,
    AlertIcon,
    VStack,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon, DragHandleIcon } from '@chakra-ui/icons';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ACCENT = '#0071E3';

interface SortableThumbProps {
    id: string;
    url: string;
    label: string;
    isActive: boolean;
    onSelect: () => void;
    onRemove: () => void;
}

const SortableThumb = ({ id, url, label, isActive, onSelect, onRemove }: SortableThumbProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : 1,
        touchAction: 'none',
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            position="relative"
            flexShrink={0}
            cursor={isDragging ? 'grabbing' : 'grab'}
            title="Drag to reorder"
            {...attributes}
            {...listeners}
        >
            <Box
                position="relative"
                borderRadius="md"
                overflow="hidden"
                border="2px solid"
                borderColor={isActive ? ACCENT : 'gray.200'}
                w="56px"
                h="44px"
                onClick={onSelect}
                bg="gray.100"
            >
                <Image src={url} alt={label} w="100%" h="100%" objectFit="cover" draggable={false} />
                <IconButton
                    aria-label={`Remove ${label}`}
                    icon={<CloseIcon boxSize={2} />}
                    variant="solid"
                    colorScheme="red"
                    position="absolute"
                    top="3px"
                    right="3px"
                    zIndex={2}
                    minW="16px"
                    w="16px"
                    h="16px"
                    p={0}
                    borderRadius="full"
                    boxShadow="none"
                    cursor="pointer"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                />
                <Box
                    position="absolute"
                    left={0}
                    bottom={0}
                    px="2px"
                    py="1px"
                    lineHeight={0}
                    bg="blackAlpha.400"
                    borderBottomLeftRadius="md"
                    borderTopRightRadius="sm"
                    pointerEvents="none"
                    aria-hidden
                >
                    <DragHandleIcon boxSize={2} color="white" />
                </Box>
                {isActive && <Box position="absolute" bottom={0} left={0} right={0} h="2px" bg={ACCENT} borderRadius="sm" />}
            </Box>
        </Box>
    );
};

export interface InvoicePageDraftStripProps {
    loading?: boolean;
    thumbnailUrls: string[];
    sortableIds: string[];
    selectedIndex0: number;
    onSelectIndex: (index0: number) => void;
    onReorder: (fromIndex0: number, toIndex0: number) => void;
    onRemoveAt: (index0: number) => void;
    onAddClick: () => void;
    error: string | null;
    onCancel: () => void;
    onStartOver: () => void;
    onReprocess: () => void;
    applyLoading: boolean;
    hasPages: boolean;
    addDisabled?: boolean;
}

const InvoicePageDraftStrip = ({
    loading = false,
    thumbnailUrls,
    sortableIds,
    selectedIndex0,
    onSelectIndex,
    onReorder,
    onRemoveAt,
    onAddClick,
    error,
    onCancel,
    onStartOver,
    onReprocess,
    applyLoading,
    hasPages,
    addDisabled = false,
}: InvoicePageDraftStripProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = sortableIds.indexOf(String(active.id));
        const newIndex = sortableIds.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) return;
        onReorder(oldIndex, newIndex);
    };

    return (
        <VStack align="stretch" spacing={2}>
            <HStack justify="space-between" align="center">
                <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                    Drag thumbnails to reorder
                </Text>
            </HStack>
            {error && (
                <Alert status="error" size="sm" borderRadius="md" py={1}>
                    <AlertIcon />
                    <Text fontSize="xs">{error}</Text>
                </Alert>
            )}
            {!hasPages && !loading && (
                <Text fontSize="xs" color="red.600">
                    No pages left. Use + to add files, or cancel.
                </Text>
            )}
            {loading && (
                <Text fontSize="xs" color="gray.500">
                    Loading page previews…
                </Text>
            )}
            <Flex align="center" gap={2} overflowX="auto" pt={1} pr={1} pb={2} pl={1}>
                {!loading && (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <Flex align="center" gap={2}>
                            <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                                {thumbnailUrls.map((url, idx) => (
                                    <SortableThumb
                                        key={sortableIds[idx] ?? `t-${idx}`}
                                        id={sortableIds[idx] ?? String(idx)}
                                        url={url}
                                        label={`Page ${idx + 1}`}
                                        isActive={selectedIndex0 === idx}
                                        onSelect={() => onSelectIndex(idx)}
                                        onRemove={() => onRemoveAt(idx)}
                                    />
                                ))}
                            </SortableContext>
                        </Flex>
                    </DndContext>
                )}
                <Tooltip label="Add pages" hasArrow>
                    <IconButton
                        aria-label="Add pages"
                        icon={<AddIcon boxSize={3.5} />}
                        variant="outline"
                        borderColor={ACCENT}
                        color={ACCENT}
                        flexShrink={0}
                        w="56px"
                        h="44px"
                        minW="56px"
                        borderRadius="md"
                        onClick={onAddClick}
                        isDisabled={addDisabled}
                    />
                </Tooltip>
            </Flex>
        </VStack>
    );
};

export default InvoicePageDraftStrip;
